import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import useCurrentUser from "./useCurrentUser";

const ViewContext = createContext();

const STORAGE_KEY = "selectedAssignee";

/**
 * Holds the single "whose tasks am I looking at" selection shared across pages.
 *
 * `selectedAssignee` is a user id (number) to scope every list/report to one
 * person, or `null` to show everyone's tasks ("All"). On a fresh session
 * (nothing persisted yet) it defaults to the logged-in user once their profile
 * resolves, so the app opens on "my tasks" without an explicit toggle.
 */
export const ViewProvider = ({ children }) => {
  const { user: currentUser } = useCurrentUser();

  // `undefined` -> not chosen yet (will default to current user)
  // `null`      -> explicitly "All users"
  // number      -> a specific user id
  const [selectedAssignee, setSelectedAssignee] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === null) return undefined;
      if (stored === "all") return null;
      const id = Number(stored);
      return Number.isNaN(id) ? undefined : id;
    } catch {
      return undefined;
    }
  });

  // Accepts a user id, an assignee object, or null ("All").
  const updateSelectedAssignee = useCallback((assignee) => {
    const id =
      assignee && typeof assignee === "object" ? assignee.id : assignee;
    const normalized = id == null ? null : Number(id);
    setSelectedAssignee(normalized);
    try {
      localStorage.setItem(
        STORAGE_KEY,
        normalized == null ? "all" : String(normalized),
      );
    } catch {
      /* ignore storage errors (e.g. private mode) */
    }
  }, []);

  // Default to the current user the first time, when nothing was persisted.
  useEffect(() => {
    if (selectedAssignee === undefined && currentUser?.id) {
      updateSelectedAssignee(currentUser.id);
    }
  }, [selectedAssignee, currentUser?.id, updateSelectedAssignee]);

  return (
    <ViewContext.Provider
      value={{
        selectedAssignee,
        updateSelectedAssignee,
      }}
    >
      {children}
    </ViewContext.Provider>
  );
};

export const useView = () => {
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error("useView must be used within a ViewProvider");
  }
  return context;
};
